'use no memo'

import { useTranslator } from '@/hooks/useTranslator'
import { useEffect, useState } from 'react'
import { Grid, Typography, Button } from '@mui/material'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { FormInput } from '../../form'
import InputDateField from '../../common/input/InputDateField'
import PhoneCodeSelection from '../../selection/PhoneCodeSelection'
import BankSelection from '../../selection/BankSelection'
import { SelectField } from '../../common/input/SelectField'
import { yupResolver } from '@hookform/resolvers/yup'
import { getCreateEmployeeSchema, IHttpError, ICreateUserPayload, useCreateEmployeeMutation } from '@/@data'
import { useNotification } from '@/hooks'
import CompanySelection from '../../selection/CompanySelection'
import dayjs from 'dayjs'
import { EmployeeConfirmRestoreDialog } from './EmployeeConfirmRestore'

export const EmployeeManualForm = ({
  onClose,
  refetch,
  companyId
}: {
  onClose: () => void
  refetch?: () => void
  companyId?: number
}) => {
  const { __ } = useTranslator()
  const n = useNotification()
  const { mutate: createEmployee } = useCreateEmployeeMutation()
  const [isRestoreDialogOpen, setRestoreDialogOpen] = useState(false)
  const [restoreEmployee, setRestoreEmployee] = useState<ICreateUserPayload | null>(null)

  const methods = useForm<any>({
    defaultValues: {
      company_id: '',
      first_name: '',
      employee_id: '',
      joined_date: '',
      contract_type: '',
      phone_code: '+84',
      phone: '',
      bank_account_name: '',
      bank_id: '',
      bank_account_number: '',
      identity_card: '',
      identity_card_issue_date: '',
      identity_card_issue_place: '',
      gender: '',
      address: '',
      birth_date: '',
      dependent: '',
      tenure: 0
    },
    resolver: yupResolver(getCreateEmployeeSchema(__)),
    mode: 'onChange'
  })

  useEffect(() => {
    if (companyId) {
      methods.reset({
        ...methods.getValues(),
        company_id: companyId
      })
    }
  }, [companyId, methods])

  // Auto-calculate tenure in months based on joined_date
  const joinedDateValue = methods.watch('joined_date')

  useEffect(() => {
    if (!joinedDateValue) {
      methods.setValue('tenure', 0)

      return
    }

    const months = dayjs().diff(dayjs(joinedDateValue), 'month')

    methods.setValue('tenure', String(months))
  }, [joinedDateValue])

  const handleRestoreDialogClose = () => {
    setRestoreDialogOpen(false)
    setRestoreEmployee(null)
  }

  const handleRestoreSuccess = () => {
    handleRestoreDialogClose()
    onClose()
    refetch?.()
  }

  const onSubmit = async (data: ICreateUserPayload) => {
    // Artificial delay before submitting
    await new Promise(resolve => setTimeout(resolve, 500))

    const sanitizeStrings = (value: any): any => {
      if (value == null) {
        return value
      }

      if (typeof value === 'string') {
        return value.trim()
      }

      if (Array.isArray(value)) {
        return value.map(sanitizeStrings)
      }

      if (typeof value === 'object') {
        const result: any = {}

        Object.entries(value).forEach(([key, val]) => {
          result[key] = sanitizeStrings(val)
        })

        return result
      }

      return value
    }

    const cleaned = sanitizeStrings(data) as ICreateUserPayload

    const payload = {
      company_id: cleaned.company_id ? Number(cleaned.company_id) : 0,
      first_name: cleaned.first_name,
      employee_id: cleaned.employee_id,
      joined_date: cleaned.joined_date,
      contract_type: cleaned.contract_type,
      phone_code: cleaned.phone_code,
      phone: cleaned.phone,
      bank_id: cleaned.bank_id,
      bank_account_number: cleaned.bank_account_number,
      bank_account_name: cleaned.bank_account_name,
      identity_card: cleaned.identity_card,
      identity_card_issue_date: cleaned.identity_card_issue_date,
      identity_card_issue_place: cleaned.identity_card_issue_place,
      gender: cleaned.gender,
      address: cleaned.address,
      birth_date: cleaned.birth_date,
      dependent: cleaned.dependent,
      tenure: cleaned.tenure
    } as any

    createEmployee(payload, {
      onSuccess: ({ data }) => {
        if (data && data.is_valid_phone_code) {
          methods.setError('phone_code', { message: __('This area code is not supported.') })
          methods.setError('phone', { message: '' })

          return
        }

        if (data && data.is_valid_phone && data.is_valid_employee) {
          methods.setError('phone', { message: __('Phone number already exists') })
          methods.setError('employee_id', { message: __('Employee ID number already exists') })

          return
        } else if (data && data.is_valid_employee) {
          methods.setError('employee_id', { message: __('Employee ID number already exists') })

          return
        } else if (data && data.is_valid_phone) {
          methods.setError('phone', { message: __('Phone number already exists') })

          return
        }

        n.success(__('User created successfully'))
        onClose()
        refetch?.()
      },
      onError: (error: IHttpError) => {
        if (error.status === 400 && error.message.length) {
          setRestoreEmployee(data)
          setRestoreDialogOpen(true)

          return
        }

        n.error(error.message)
      }
    })
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <Grid container spacing={4}>
          <Grid size={{ xs: 12, sm: 6, md: 6 }}>
            <Controller
              name='company_id'
              control={methods.control}
              render={({ field, fieldState }) => (
                <CompanySelection
                  {...field}
                  fullWidth
                  label={__('Employer')}
                  onClear={() => methods.setValue('company_id', 0)}
                  required
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  disabled={!!companyId}
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <div className='text-[15px] font-medium leading-[15px] mb-2 text-[#2f2b3de6]'>
              {__('Employee Name')} <span className='text-[#FF4C51]'>*</span>
            </div>
            <FormInput
              name='first_name'
              placeholder={__('Enter Full Name')}
              control={methods.control}
              fullWidth
              onClear={() => methods.setValue('first_name', '')}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <div className='text-[15px] font-medium leading-[15px] mb-2 text-[#2f2b3de6]'>
              {__('Employee Code')} <span className='text-[#FF4C51]'>*</span>
            </div>
            <FormInput
              name='employee_id'
              placeholder={__('Enter Employee Code')}
              control={methods.control}
              fullWidth
              onClear={() => methods.setValue('employee_id', '')}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant='body2' fontWeight={500}>
              {__('Joined Date')}
            </Typography>
            <Controller
              name='joined_date'
              control={methods.control}
              render={({ field }) => (
                <InputDateField
                  value={field.value}
                  placeholder={__('Select Date')}
                  onChange={field.onChange}
                  className='rounded-full py-2'
                  placeholderFontSize='16px'
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }} gap={2}>
            <Grid size={{ xs: 12, sm: 12 }}>
              <Typography variant='body2' fontWeight={500}>
                {__('Phone')} <span style={{ color: 'red' }}>*</span>
              </Typography>
            </Grid>
            <Grid container sx={{ display: 'flex', flexDirection: 'row', gap: 2, width: '100%' }}>
              <Grid size={{ xs: 12, sm: 4 }} sx={{ flex: 1 }}>
                <Controller
                  name='phone_code'
                  control={methods.control}
                  render={({ field, fieldState }) => (
                    <PhoneCodeSelection
                      {...field}
                      errorMessage={fieldState.error?.message}
                      error={!!fieldState.error?.message}
                      placeholder={__('+1 (*)')}
                      onClear={() => methods.setValue('phone_code', '')}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 7 }} sx={{ flex: 1 }} className='flex-1'>
                <FormInput
                  name='phone'
                  placeholder={__('Enter Phone')}
                  type='number'
                  control={methods.control}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <FormInput
              label={__('Tenure')}
              name='tenure'
              placeholder={__('Enter Tenure')}
              control={methods.control}
              fullWidth
              disabled
              slotProps={{
                input: {
                  endAdornment: <span className='text-[#2F2B3D66] text-[14px] ml-2'>{__('months')}</span>
                }
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 12 }}>
            <div className='text-[15px] font-medium leading-[15px] mb-2 text-[#2f2b3de6]'>
              {__('Bank Account Name')} <span className='text-[#FF4C51]'>*</span>
            </div>
            <FormInput
              name='bank_account_name'
              fullWidth
              control={methods.control}
              placeholder={__('Enter Bank Account Name')}
              transformValue={value => (typeof value === 'string' ? value.toUpperCase() : value)}
              onClear={() => methods.setValue('bank_account_name', '')}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name='bank_id'
              control={methods.control}
              render={({ field, fieldState }) => (
                <BankSelection
                  {...field}
                  fullWidth
                  required
                  label={__('Bank Name')}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  onClear={() => methods.setValue('bank_id', '')}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <div className='text-[15px] font-medium leading-[15px] mb-2 text-[#2f2b3de6]'>
              {__('Bank Account')} <span className='text-[#FF4C51]'>*</span>
            </div>
            <FormInput
              name='bank_account_number'
              fullWidth
              control={methods.control}
              placeholder={__('Enter Bank Account')}
              type='number'
              onClear={() => methods.setValue('bank_account_number', '')}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <FormInput
              label={__('Identity Card')}
              name='identity_card'
              fullWidth
              control={methods.control}
              placeholder={__('Enter Identity Card')}
              onClear={() => methods.setValue('identity_card', '')}
              type='number'
              slotProps={{
                input: {
                  sx: {
                    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                      '-webkit-appearance': 'none',
                      margin: 0
                    },
                    '&[type=number]': {
                      '-moz-appearance': 'textfield'
                    }
                  }
                }
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant='body2' fontWeight={500}>
              {__('Identity Card Issue Date')}
            </Typography>
            <Controller
              name='identity_card_issue_date'
              control={methods.control}
              render={({ field }) => (
                <InputDateField
                  value={field.value}
                  placeholder={__('Select Date')}
                  onChange={field.onChange}
                  className='rounded-full py-2'
                  placeholderFontSize='16px'
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <FormInput
              label={__('Identity Card Issue Place')}
              name='identity_card_issue_place'
              fullWidth
              control={methods.control}
              placeholder={__('Enter Identity Card Issue Place')}
              onClear={() => methods.setValue('identity_card_issue_place', '')}
            />
          </Grid>

          {/* Gender */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant='body2' fontWeight={500}>
              {__('Gender')}
            </Typography>
            <Controller
              name='gender'
              control={methods.control}
              render={({ field }) => (
                <SelectField
                  {...field}
                  className='w-full py-2'
                  options={[
                    { label: __('Male'), value: 'male' },
                    { label: __('Female'), value: 'female' }
                  ]}
                  displayEmpty
                  renderValue={value => {
                    if (!value) {
                      return <span style={{ color: '#999' }}>{__('Select Gender')}</span>
                    }

                    return value === 'male' ? __('Male') : __('Female')
                  }}
                  fullWidth
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <FormInput
              label={__('Address')}
              name='address'
              fullWidth
              control={methods.control}
              placeholder={__('Enter Address')}
              onClear={() => methods.setValue('address', '')}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant='body2' fontWeight={500}>
              {__('D.O.B')}
            </Typography>
            <Controller
              name='birth_date'
              control={methods.control}
              render={({ field }) => (
                <InputDateField
                  value={field.value}
                  placeholder={__('Select Date')}
                  onChange={field.onChange}
                  className='rounded-full py-2'
                  placeholderFontSize='16px'
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormInput
              label={__('Dependent')}
              name='dependent'
              placeholder={__('Enter Dependent')}
              control={methods.control}
              onClear={() => methods.setValue('dependent', '')}
              fullWidth
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <FormInput
              label={__('Contract Type')}
              name='contract_type'
              placeholder={__('Enter Contract Type')}
              control={methods.control}
              onClear={() => methods.setValue('contract_type', '')}
              fullWidth
            />
          </Grid>
        </Grid>
        <div className='flex flex-row gap-4 mt-8'>
          <Button
            variant='contained'
            className='text-white text-[15px] font-medium leading-[22px] bg-[#3551F2] shadow-none rounded-full'
            type='submit'
            disabled={!methods.formState.isValid}
          >
            {methods.formState.isSubmitting ? __('Waiting...') : __('Submit')}
          </Button>
          <Button
            variant='contained'
            className='text-[#FF4C51] text-[15px] font-medium leading-[22px] bg-[#FCE2E3] shadow-none rounded-full'
            onClick={onClose}
          >
            {__('Cancel')}
          </Button>
        </div>
      </form>
      <EmployeeConfirmRestoreDialog
        open={isRestoreDialogOpen}
        onClose={handleRestoreDialogClose}
        employee={restoreEmployee}
        onSuccess={handleRestoreSuccess}
        refetch={refetch}
      />
    </FormProvider>
  )
}
